/**
 * main.ts
 */
import { createApp } from 'vue';
import App from './App.vue';
import router from './router';

// A 138-icon subset of Material Design Icons instead of all 7,447. The full
// set costs 322 KB of CSS rules and a 403 KB woff2 on the critical path for
// ~2% utilisation. Generated by scripts/build-mdi-subset.mjs (npm run
// build:icons) and committed; the build verifies it still covers every icon
// in use. See that script for why it isn't generated at build time.
import './styles/mdi-subset.css';
import './styles/tokens.scss';
import './styles/utilities.scss';
import './styles/components.scss';
import './styles/overview.scss';
import 'vue-toastification/dist/index.css';

import Toast from 'vue-toastification';
import { VueQueryPlugin } from '@tanstack/vue-query';
import { registerPlugins } from '@/plugins';
import { auth0 } from '@/plugins/auth0';
import { queryClient } from '@/plugins/queryClient';
import { clearPersistedQueryCache } from '@/plugins/queryPersister';
import { globalState } from '@/stores/globalState';
import { setupGlobalErrorHandling } from '@/composables/useErrorHandling';

// We intentionally do NOT restore the query cache from localStorage on boot, so
// a full page reload starts cold: every section — the charts AND the slower,
// uncached account details — loads fresh and shimmers together. Restoring made
// the cache-instant charts paint immediately while accounts were still
// fetching, which read as a half-loaded page. In-app navigation stays instant
// regardless, because TanStack keeps query data in memory for the session.
// (Wipe any snapshot left by older builds that did persist.)
clearPersistedQueryCache();

const app = createApp(App);

registerPlugins(app);
app.use(auth0); // installs Auth0 SDK plugin
app.use(VueQueryPlugin, { queryClient }); // installs TanStack Query

// TanStack Query DevTools can be added later if needed
// For now, use browser dev tools to inspect cache via queryClient
if (import.meta.env.DEV) {
  console.log('🚀 TanStack Query caching enabled - use browser console: window.queryClient to inspect cache')
  // Make queryClient available globally for debugging
  ;(window as any).queryClient = queryClient
  
  // Add helper functions for debugging
  ;(window as any).inspectCache = () => {
    const queries = queryClient.getQueryCache().getAll()
    console.log('📊 Current cache state:')
    queries.forEach(query => {
      console.log(`  ${query.queryHash}:`, {
        state: query.state.status,
        isPending: query.state.fetchStatus === 'fetching',
        hasData: !!query.state.data,
        dataAge: query.state.dataUpdatedAt ? Date.now() - query.state.dataUpdatedAt : 'never',
        isStale: query.isStale()
      })
    })
    return queries
  }
  
  ;(window as any).clearQueryCache = () => {
    queryClient.clear()
    console.log('🧹 Query cache cleared')
  }
}

app.use(router);
app.use(Toast, {
  position: 'bottom-right',
  timeout: 4000,
  closeOnClick: true,
  draggable: true,
  hideProgressBar: false,
});

// Set up global error handling
setupGlobalErrorHandling();

app.config.errorHandler = (err, instance, info) => {
  console.error('[Vue error]', err, { info, instance });
};

app.mount('#app');
